import type { FC } from 'react';

import { copyrightStyle } from '@/features/storefront/appearance';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { useShopContent } from '@/features/storefront/content/useContent';
import { LanguageSwitcherSlot } from '@/features/storefront/i18n/LanguageSwitcher';
import { AppStoreBadges } from '@/features/storefront/layouts/chrome/FooterExtras';
import { InstallAppSlot } from '@/features/storefront/pwa/InstallAppSlot';
import {
  BlogLinks,
  MenuLink,
  PageLinks,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { menuItems, type FooterProps } from '@/features/storefront/layouts/chrome/types';

/** Columns under a thick stripe of the theme's primary colour. */
export const Footer: FC<FooterProps> = ({ storeName, footer, description, menu, preview }) => {
  const items = menuItems(menu);
  const copy = useShopContent();
  return (
    <footer
      className="border-t-4 py-12 text-sm"
      style={{ borderColor: 'var(--storefront-primary)', color: 'var(--storefront-muted)' }}
    >
      <StorefrontContainer className="grid gap-10 text-start sm:grid-cols-3">
        <div>
          <p
            className="text-xl font-black uppercase tracking-tight"
            style={{ color: 'var(--storefront-text)' }}
          >
            {storeName}
          </p>
          {description ? <p className="mt-3">{description}</p> : null}
        </div>
        <div>
          {items.length > 0 ? (
            <nav aria-label={menu?.name} className="flex flex-col items-start gap-2.5">
              {items.map((item) => (
                <MenuLink
                  key={`${item.label}:${item.url}`}
                  item={item}
                  preview={preview}
                  className="font-medium hover:opacity-70"
                />
              ))}
            </nav>
          ) : null}
        </div>
        <div>
          {preview ? null : (
            <>
              <PageLinks className="mb-2 flex-col items-start justify-start gap-2.5" />
              <BlogLinks className="flex-col items-start justify-start gap-2.5" />
            </>
          )}
        </div>
      </StorefrontContainer>
      <StorefrontContainer className="mt-10 text-start text-xs">
        <InstallAppSlot preview={preview} className="mb-3" />
        <AppStoreBadges footer={footer} preview={preview} className="mb-3" />
        <LanguageSwitcherSlot placement="footer" preview={preview} className="mb-3" />
        <p style={copyrightStyle(footer)}>
          {storeName} · {copy('theme.powered_by', { brand: 'NeonCarts' })}
        </p>
      </StorefrontContainer>
    </footer>
  );
};
